/**
 * Enhanced Dashboard Page
 * Features vibrant colors, shimmer loading effects, improved responsiveness
 * and modern design with gradient backgrounds and animations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserCog,
  TrendingUp,
  Calendar,
  CheckCircle,
  PoundSterling,
  Grid3x3,
  Wrench,
  Activity,
} from 'lucide-react';
import { realAPI } from '../api/realEndpoints';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../utils/helpers';
import Card from '../components/Card';
import Shimmer from '../components/Shimmer';
import UsersAnalyticsGraph from '../components/UsersAnalyticsGraph';
import BookingsGraph from '../components/BookingsGraph';

export default function Dashboard() {
  const navigate = useNavigate();
  const { refreshTrigger, dashboardData } = useApp();
  const [metrics, setMetrics] = useState({
    totalCustomers: 0,
    totalProviders: 0,
    subscribedProviders: 0,
    totalBooking: 0,
    completedBookings: 0,
    totalRevenue: 0,
    totalServices: 0,
    totalCategory: 0,
  });
  const [graphData, setGraphData] = useState({
    usersGraph: null,
    bookingsGraph: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 🔹 Create promises (cached data resolves instantly)
      const metricsPromise = dashboardData?.metrics
        ? Promise.resolve(dashboardData.metrics)
        : realAPI.dashboard.getMetrics();

      const usersGraphPromise = dashboardData?.usersGraph
        ? Promise.resolve(dashboardData.usersGraph)
        : realAPI.dashboard.getUsersGraph('daily');

      const bookingsGraphPromise = dashboardData?.bookingsGraph
        ? Promise.resolve(dashboardData.bookingsGraph)
        : realAPI.dashboard.getBookingsGraph('daily');

      // 🚀 Fetch everything in parallel
      const [metricsResponse, usersGraphResponse, bookingsGraphResponse] = await Promise.all([
        metricsPromise,
        usersGraphPromise,
        bookingsGraphPromise,
      ]);

      // 🔹 Update metrics
      setMetrics({
        totalCustomers: metricsResponse?.totalCustomers ?? 0,
        totalProviders: metricsResponse?.totalProviders ?? 0,
        subscribedProviders: metricsResponse?.subscribedProviders ?? 0,
        totalBooking: metricsResponse?.totalBooking ?? 0,
        completedBookings: metricsResponse?.completedBookings ?? 0,
        totalRevenue: metricsResponse?.totalRevenue ?? 0,
        totalServices: metricsResponse?.totalServices ?? 0,
        totalCategory: metricsResponse?.totalCategory ?? 0,
      });

      // 🔹 Update graph data
      setGraphData({
        usersGraph: usersGraphResponse ?? null,
        bookingsGraph: bookingsGraphResponse ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);

      // 🔹 Fail-safe defaults
      setMetrics({
        totalCustomers: 0,
        totalProviders: 0,
        subscribedProviders: 0,
        totalBooking: 0,
        completedBookings: 0,
        totalRevenue: 0,
        totalServices: 0,
        totalCategory: 0,
      });

      setGraphData({
        usersGraph: null,
        bookingsGraph: null,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dashboardData]);
  // Empty dependencies - dashboardData accessed via closure

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, refreshTrigger]);

  const metricCards = [
    {
      title: 'Total Customers',
      value: metrics.totalCustomers,
      icon: Users,
      gradient: 'from-blue-500 to-cyan-400',
      bgColor:
        'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
      iconBg: 'bg-blue-500',
      textColor: 'text-blue-600 dark:text-blue-400',
      link: '/customers',
      trend: { value: 12, isUp: true },
    },
    {
      title: 'Total Providers',
      value: metrics.totalProviders,
      icon: UserCog,
      gradient: 'from-purple-500 to-pink-400',
      bgColor:
        'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20',
      iconBg: 'bg-purple-500',
      textColor: 'text-purple-600 dark:text-purple-400',
      link: '/providers',
      trend: { value: 8, isUp: true },
    },
    {
      title: 'Subscribed Providers',
      value: metrics.subscribedProviders,
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-teal-400',
      bgColor:
        'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
      iconBg: 'bg-green-500',
      textColor: 'text-green-600 dark:text-green-400',
      link: null,
      trend: { value: 23, isUp: true },
    },
    {
      title: 'Total Bookings',
      value: metrics.totalBooking,
      icon: Calendar,
      gradient: 'from-orange-500 to-red-400',
      bgColor:
        'bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20',
      iconBg: 'bg-orange-500',
      textColor: 'text-orange-600 dark:text-orange-400',
      link: '/bookings',
      trend: { value: 5, isUp: false },
    },
    {
      title: 'Completed Bookings',
      value: metrics.completedBookings,
      icon: CheckCircle,
      gradient: 'from-teal-500 to-green-400',
      bgColor:
        'bg-gradient-to-br from-teal-50 to-green-50 dark:from-teal-900/20 dark:to-green-900/20',
      iconBg: 'bg-teal-500',
      textColor: 'text-teal-600 dark:text-teal-400',
      link: '/bookings?status=completed',
      onClick: () => {
        // Set flag to indicate navigation from dashboard completed bookings
        sessionStorage.setItem('fromDashboardCompleted', 'true');
        navigate('/bookings?status=completed');
      },
      trend: { value: 15, isUp: true },
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(metrics.totalRevenue),
      icon: PoundSterling,
      gradient: 'from-yellow-500 to-amber-400',
      bgColor:
        'bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20',
      iconBg: 'bg-yellow-500',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      link: '/transactions',
      trend: { value: 18, isUp: true },
    },
    {
      title: 'Total Categories',
      value: metrics.totalCategory,
      icon: Grid3x3,
      gradient: 'from-indigo-500 to-blue-400',
      bgColor:
        'bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
      iconBg: 'bg-indigo-500',
      textColor: 'text-indigo-600 dark:text-indigo-400',
      link: '/categories',
      trend: { value: 3, isUp: true },
    },
    {
      title: 'Total Services',
      value: metrics.totalServices,
      icon: Wrench,
      gradient: 'from-cyan-500 to-blue-400',
      bgColor:
        'bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20',
      iconBg: 'bg-cyan-500',
      textColor: 'text-cyan-600 dark:text-cyan-400',
      link: '/services',
      trend: { value: 10, isUp: true },
    },
  ];

  // Shimmer loading skeleton
  const ShimmerCard = () => (
    <Card variant="gradient" className="relative overflow-hidden p-2">
      <div className="flex items-center justify-between">
        <div className="space-y-1 flex-1 pr-2">
          <Shimmer variant="text" className="w-16 h-3.5" />
          <Shimmer variant="text" className="w-12 h-7" />
        </div>
        <Shimmer variant="rectangle" className="w-7 h-7 rounded-lg flex-shrink-0" />
      </div>
    </Card>
  );

  return (
    <div className="dark:bg-gray-900 p-4 lg:p-6 min-h-[calc(100dvh-4rem)] lg:min-h-0">
      {/* Metrics Grid or Loading Shimmer */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
          {[...Array(8)].map((_, i) => (
            <ShimmerCard key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
          {metricCards.map((metric, index) => (
            <div
              key={metric.title}
              onClick={() => metric.link && navigate(metric.link)}
              className={`
                transform transition-all duration-500 ease-out
                ${metric.link ? 'cursor-pointer hover:scale-105' : ''}
                animate-fade-in
              `}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <Card
                variant="gradient"
                className={`${metric.bgColor} border-0 relative overflow-hidden group p-2`}
              >
                {/* Background decoration */}
                <div
                  className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${metric.gradient} opacity-10 rounded-full -mr-8 -mt-8 group-hover:opacity-20 transition-opacity`}
                ></div>

                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-2">
                      <p className={`text-sm font-semibold ${metric.textColor} mb-1 opacity-80`}>
                        {metric.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 leading-tight dark:text-white">
                        {metric.value}
                      </p>
                    </div>
                    <div
                      className={`${metric.iconBg} p-1.5 rounded-lg shadow-lg transform group-hover:scale-110 transition-transform flex-shrink-0`}
                    >
                      <metric.icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Graphs Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Unified Users Analytics Graph */}
        <div className="h-[20rem] lg:h-[28rem]">
          <UsersAnalyticsGraph data={graphData.usersGraph} isLoading={isLoading} />
        </div>

        {/* Bookings Analytics Graph */}
        <div className="h-[20rem] lg:h-[28rem]">
          <BookingsGraph data={graphData.bookingsGraph} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}
